import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Form, Input, Button, Switch } from 'antd';
import { DataCrafterTemplateFormStore, DataCrafterOutputVariantsStore } from '../../stores';
import { CreateUpdateDataCrafterOutputVariantRequest, DataCrafterOutputVariant } from '../../types';
import './CreateEditOutputVariantDialog.less';

interface Props {
    formStore: DataCrafterTemplateFormStore;
    outputVariantsStore: DataCrafterOutputVariantsStore;
}

interface FormValues {
    name: string;
    condition: string;
    isDefault: boolean;
}

/**
 * Dialog component for creating and editing output variants.
 * Allows user to modify variant name, condition and default status.
 * The submit action creates new variant or updates existing based on editableOutputVariant.
 */
const CreateEditOutputVariantDialog: React.FC<Props> = ({ formStore, outputVariantsStore }) => {
    const [form] = Form.useForm<FormValues>();

    // Determine if we are in edit mode based on whether we have an editable variant
    const isEditMode = React.useMemo(
        () => Boolean(outputVariantsStore.editableOutputVariant),
        [outputVariantsStore.editableOutputVariant]
    );

    // Set initial form values when dialog opens or editable variant changes
    React.useEffect(() => {
        if (outputVariantsStore.outputVariantDialogVisible) {
            if (outputVariantsStore.editableOutputVariant) {
                // Edit mode - populate form with existing variant data
                form.setFieldsValue({
                    name: outputVariantsStore.editableOutputVariant.name,
                    condition: outputVariantsStore.editableOutputVariant.condition,
                    isDefault: outputVariantsStore.editableOutputVariant.isDefault
                });
            } else {
                // Create mode - reset form to default values
                form.setFieldsValue({
                    name: '',
                    condition: '',
                    isDefault: false
                });
            }
        }
    }, [form, outputVariantsStore.outputVariantDialogVisible, outputVariantsStore.editableOutputVariant]);

    // Handle form submission
    const onFinish = async (values: FormValues) => {
        // Create the request object with form values
        // Note: columns and script refs will be handled separately by the backend or inherited from template
        const request = new CreateUpdateDataCrafterOutputVariantRequest(
            values.name,
            values.condition,
            outputVariantsStore.editableOutputVariant?.columns || [], // Keep existing columns or empty array
            values.isDefault,
            outputVariantsStore.editableOutputVariant?.scriptRefId // Keep latest generated script reference
        );

        let success = false;

        if (isEditMode && outputVariantsStore.editableOutputVariant?.variantId) {
            // Update existing variant
            const result = outputVariantsStore.updateOutputVariantLocal(
                outputVariantsStore.editableOutputVariant.variantId,
                request
            );
            success = Boolean(result);
        } else {
            // Create new variant
            const result = outputVariantsStore.createOutputVariantLocal(
                request,
                formStore.outputColumns,
                formStore.selectedTemplate?.id || '',
                formStore.tempTemplateId
            );
            success = Boolean(result);
        }

        if (success) {
            handleClose();
        }
    };

    // Handle dialog close
    const handleClose = () => {
        form.resetFields();
        outputVariantsStore.closeOutputVariantDialog();
    };

    // Validate variant name uniqueness
    const validateVariantName = (_: unknown, value: string) => {
        if (!value?.trim()) {
            return Promise.resolve(); // Required validation will handle empty values
        }

        const trimmedValue = value.trim();
        const existingVariant = outputVariantsStore.outputVariants.find(
            (variant: DataCrafterOutputVariant) =>
                variant.name.toLowerCase() === trimmedValue.toLowerCase() &&
                variant.variantId !== outputVariantsStore.editableOutputVariant?.variantId
        );

        if (existingVariant) {
            return Promise.reject('A variant with this name already exists');
        }

        return Promise.resolve();
    };

    return (
        <Modal
            className="alpha-portal-dialog create-edit-output-variant-dialog"
            title={isEditMode ? 'Edit Output Variant' : 'Create Output Variant'}
            visible={outputVariantsStore.outputVariantDialogVisible}
            width={600}
            onCancel={handleClose}
            maskClosable={false}
            closable={false}
            destroyOnClose
            centered
            footer={[
                <Button
                    form="create-edit-output-variant-form"
                    data-id="create-edit-output-variant-cancel"
                    key="create-edit-output-variant-cancel"
                    className="light"
                    size="large"
                    onClick={handleClose}
                >
                    Cancel
                </Button>,
                <Button
                    form="create-edit-output-variant-form"
                    data-id="create-edit-output-variant-submit"
                    key="create-edit-output-variant-submit"
                    size="large"
                    type="primary"
                    htmlType="submit"
                >
                    {isEditMode ? 'Update' : 'Create'}
                </Button>
            ]}
        >
            <Form
                id="create-edit-output-variant-form"
                layout="vertical"
                form={form}
                onFinish={onFinish}
                preserve={false}
            >
                <Form.Item
                    label="Variant Name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            message: 'Please enter variant name!',
                            whitespace: true
                        },
                        {
                            validator: validateVariantName
                        }
                    ]}
                >
                    <Input
                        size="large"
                        placeholder="Enter a descriptive name for this variant"
                        autoFocus={!isEditMode}
                    />
                </Form.Item>

                <Form.Item
                    label="Condition"
                    name="condition"
                    rules={[
                        {
                            required: true,
                            message: 'Please enter condition!',
                            whitespace: true
                        }
                    ]}
                    extra="Define the condition that determines when this variant should be used"
                >
                    <Input.TextArea
                        size="large"
                        rows={3}
                        placeholder="e.g., document_type == 'invoice' and amount > 1000"
                    />
                </Form.Item>

                <Form.Item
                    label="Default Variant"
                    name="isDefault"
                    valuePropName="checked"
                    extra="Default variant will be used when no other conditions match"
                >
                    <Switch />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(CreateEditOutputVariantDialog);
